import React, { Suspense, lazy, useContext, useEffect } from 'react';
import clsx from 'clsx';
import DrawerContext from '~/contexts/Drawer';
import { Router, Switch, Route } from 'react-router-dom';

import Container from '@material-ui/core/Container';

import Addon from '~/pages/Addon';
import Files from '~/pages/Files';
import Push from '~/pages/Push';
import Dashboards from '~/pages/Dashboards';
import Specialties from '~/pages/Specialties';
import Packs from '~/pages/Packs';
import Products from '~/pages/Products';
import Pins from '~/pages/Pins';
import Logs from '~/pages/Logs';
import EpayTransactions from '~/pages/EpayTransactions';
import DoctorSchedules from '~/pages/DoctorSchedules';
import Chats from '~/pages/Chats';
import Partner from '~/pages/Partner';
import Subscription from '~/pages/Subscription';
import Faq from '~/pages/Faq';
import Peoples from '~/pages/Peoples';
import PackageBillings from '~/pages/PackageBilling';
import PlanBillings from '~/pages/PlanBilling';
import PartnersDashboard from '~/pages/PartnersDashboard';
import TourImage from '~/pages/TourImage';
import CurrentUser from '~/contexts/CurrentUser';
import Loading from '~/components/Loading';
import Header from '~/containers/Header';
import Menu from '~/containers/Menu';
import history from '~/providers/history';
import Cards from '~/pages/Cards';
import Coupon from '~/pages/Coupon';
import MedicalAppointments from '~/pages/MedicalAppointments';
import MedicalAppointmentsReports from '~/pages/MedicalAppointmentsReports';
import Corporation from '~/pages/Corporation';
import AppointmentCategories from '~/pages/AppointmentCategories';
import AppointmentTags from '~/pages/AppointmentTags';
import NotFoundPage from '~/pages/NotFoundPage';
import LoadingPage from '~/pages/LoadingPage';
import Reports from '~/pages/Reports';
import PendingFiles from '~/pages/PendingFiles';
import WhatsappCampaign from '~/pages/WhatsappCampaign';
import AppointmentConfirmations from '~/pages/AppointmentConfirmations';
import PreRegistrationCampaign from '~/pages/PreRegistrationCampaign';
import InfobipAccess from '~/pages/InfobipAccess';
import {
  login,
  peoples,
  addon,
  subscription,
  dashboards,
  specialties,
  packs,
  products,
  pins,
  epayTransactions,
  schedules,
  chats,
  coupon,
  files,
  sendPush,
  cards,
  tourImage,
  partner,
  packageBillings,
  planBillings,
  medicalAppointments,
  medicalAppointmentsReports,
  corporation,
  appointmentCategories,
  appointmentTags,
  faq,
  reports,
  pendingFiles,
  showLog,
  whatsappCampaign,
  appointmentConfirmations,
  preRegistrationCampaign,
  infobipAccess,
} from '~/config/routes';
import OneSignal from 'react-onesignal';
import useStyles from './styles';
import Storage from '~/providers/Storage';
import { AbilityContext } from '~/contexts/Can';
import { buildAbilityFor } from '~/config/ability';
import { AttendantWebSocketProvider } from '~/contexts/AttendantWebSocket';

const NODE_ENV = process.env.NODE_ENV;

const Main = React.memo(function Main() {
  const classes = useStyles();

  return (
    <>
      <div className={clsx('no-printable', classes.appBarSpacer)} />
      <Container maxWidth="xl" className={classes.container}>
        <Suspense fallback={<Loading />}>
          <Switch>
            <Route path="/" exact component={Peoples} />
            <Route path={peoples.spec} component={Peoples} />
            <Route path={addon.spec} component={Addon} />
            <Route path={cards.spec} component={Cards} />
            <Route path={coupon.spec} component={Coupon} />
            <Route path={subscription.spec} component={Subscription} />
            <Route path={faq.spec} component={Faq} />
            <Route path={dashboards.spec} component={Dashboards} />
            <Route path={schedules.spec} component={DoctorSchedules} />
            <Route path={specialties.spec} component={Specialties} />
            <Route path={packs.spec} component={Packs} />
            <Route path={products.spec} component={Products} />
            <Route path={pins.spec} component={Pins} />
            <Route path={showLog.spec} component={Logs} />
            <Route path={epayTransactions.spec} component={EpayTransactions} />
            <Route path={chats.spec} component={Chats} />
            <Route path={files.spec} component={Files} />
            <Route path={sendPush.spec} component={Push} />
            <Route path={tourImage.spec} component={TourImage} />
            <Route path={partner.spec} component={Partner} />
            <Route path={packageBillings.spec} component={PackageBillings} />
            <Route path={planBillings.spec} component={PlanBillings} />
            <Route
              path={medicalAppointments.spec}
              component={MedicalAppointments}
            />
            <Route
              path={medicalAppointmentsReports.spec}
              component={MedicalAppointmentsReports}
            />
            <Route path={corporation.spec} component={Corporation} />
            <Route
              path={appointmentCategories.spec}
              component={AppointmentCategories}
            />
            <Route path={appointmentTags.spec} component={AppointmentTags} />
            <Route path={reports.spec} component={Reports} />
            <Route path={pendingFiles.spec} component={PendingFiles} />
            <Route path={whatsappCampaign.spec} component={WhatsappCampaign} />
            <Route path={appointmentConfirmations.spec} component={AppointmentConfirmations} />
            <Route path={preRegistrationCampaign.spec} component={PreRegistrationCampaign} />
            <Route path={infobipAccess.spec} component={InfobipAccess} />
            <Route path="*" exact component={NotFoundPage} />
          </Switch>
        </Suspense>
      </Container>
    </>
  );
});

function Pages() {
  const { loading, user, setUser } = useContext(CurrentUser);
  const { open, setOpen } = useContext(DrawerContext);
  const classes = useStyles();
  const ability = buildAbilityFor(user);

  async function logout() {
    await Storage.removeItem(Storage.userKey);
    await Storage.removeItem(Storage.tokenKey);
    await Storage.removeItem(Storage.refreshTokenKey);
    setUser(null);
    history.replace('/');
  }

  useEffect(() => {
    if (history.location.pathname === '/logout') logout();
  }, []);

  if (!user && loading) {
    return (
      <Router history={history}>
        <Suspense fallback={<Loading />}>
          <Route path="*" exact component={LoadingPage} />
        </Suspense>
      </Router>
    );
  }

  if (!user) {
    return (
      <Router history={history}>
        <Suspense fallback={<Loading />}>
          <Route
            path={login.spec}
            component={lazy(() => import('~/pages/Login'))}
          />
        </Suspense>
      </Router>
    );
  } else {
    if (NODE_ENV === 'production') {
      setTimeout(function () {
        OneSignal.sendTag('tag', `GeoLife${user.uuid}`);
      }, 5000);
    }

    if (history.location.pathname === '/profile') {
      history.push(`/clientes/edit/${user.id}`);
    }

    if (user.role === 'partner') {
      return (
        <Router history={history}>
          <Suspense fallback={<Loading />}>
            <Menu open={open} />
            <main
              className={clsx(classes.content, open && classes.contentShift)}
            >
              <Route component={PartnersDashboard} />
            </main>
          </Suspense>
        </Router>
      );
    } else {
      return (
        <Router history={history}>
          <AttendantWebSocketProvider>
            <AbilityContext.Provider value={ability}>
              <div className={classes.root}>
                <Header open={open} className="no-printable" />
                <Menu open={open} setOpen={setOpen} />
                <main
                  className={clsx(
                    classes.content,
                    open && classes.contentShift
                  )}
                >
                  <Main />
                </main>
              </div>
            </AbilityContext.Provider>
          </AttendantWebSocketProvider>
        </Router>
      );
    }
  }
}

export default Pages;
